import React, { useState, useRef } from 'react';
import { Upload, X, FileText, CheckCircle, Save, AlertTriangle, Minus, Plus } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';

const SeaExportForm = ({ onClose, onSubmit, customers = [], existingBookings = [], onCustomerChange = () => {} }) => {
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorTitle, setErrorTitle] = useState('');
  const [pdfRemoveConfirm, setPdfRemoveConfirm] = useState(null);
  const [containerNoList, setContainerNoList] = useState(['']);
  const [containerNoItemErrors, setContainerNoItemErrors] = useState([]);
  
  // Ref for file input to reset it
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    customerId: '',
    portCode: '',
    sbNo: '',
    sbDate: '',
    pkg: '',
    gwt: '',
    portOfLoading: '',
    stateOfOrigin: '',
    portOfDischarge: '',
    portOfDischargeCode: '',
    portOfFinalDestinationCode: '',
    exporterName: '',
    exporterAddress: '',
    countryOfFinalDestination: '',
    portOfFinalDestination: '',
    consigneeName: '',
    buyerName: '',
    mode: '',
    lut: '',
    fobValue: '',
    dbkClaim: '',
    igstAmt: '',
    cessAmt: '',
    igstValue: '',
    rodtepAmt: '',
    rosctlAmt: '',
    invNo: '',
    invAmt: '',
    currency: '',
    gstNo: '',
    leoDate: '',
    invoiceNoDt: '',
    blNumber: '',
    containerNo: '',
    containerQty: '1',
    leoPdf: null,
    blPdf: null,
    invPlPdf: null
  });

  // Field validation errors
  const [fieldErrors, setFieldErrors] = useState({
    blNumber: '',
    containerNo: '',
    containerQty: ''
  });

  const openPdfRemoveConfirm = (config) => {
    setPdfRemoveConfirm(config);
  };

  const closePdfRemoveConfirm = () => {
    setPdfRemoveConfirm(null);
  };

  const confirmPdfRemove = () => {
    if (pdfRemoveConfirm?.onConfirm) {
      pdfRemoveConfirm.onConfirm();
    }
    setPdfRemoveConfirm(null);
  };

  const splitPdfValues = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    if (value instanceof File) return [value];
    return String(value)
      .split(/\s*&\s*|\s*,\s*/)
      .map(v => v.trim())
      .filter(Boolean);
  };

  const getSbCount = () => {
    const sbRaw = formData.sbNo || '';
    const list = String(sbRaw)
      .split(/\s*&\s*|\s*,\s*/)
      .map(v => v.trim())
      .filter(Boolean);
    return Math.max(1, list.length);
  };
  
  // Validate individual field
  const validateField = (name, value) => {
    let error = '';

    if (name === 'blNumber') {
      error = '';
    } else if (name === 'containerNo') {
      const trimmedValue = value?.trim();
      if (trimmedValue && trimmedValue.length === 11) {
        // Check format: 4 letters + 7 numbers
        if (!/^[A-Z]{4}\d{7}$/.test(trimmedValue)) {
          error = 'Enter Correct Container No.';
        } else {
          // Check if 4th character is U, J, or Z
          const fourthChar = trimmedValue[3];
          if (!['U', 'J', 'Z'].includes(fourthChar)) {
            error = 'Enter Correct Container No.';
          }
        }
      }
    } else if (name === 'containerQty') {
      const trimmedValue = value?.trim();
      if (trimmedValue && isNaN(trimmedValue)) {
        error = 'Container Qty must be a number';
      } else if (trimmedValue && parseInt(trimmedValue, 10) < 1) {
        error = 'Container Qty must be at least 1';
      }
    }

    setFieldErrors(prev => ({
      ...prev,
      [name]: error
    }));

    return error === '';
  };

  const validateContainerNoValue = (value) => {
    const trimmedValue = String(value || '').trim().toUpperCase();
    if (!trimmedValue) return '';

    const containerPattern = /^[A-Z]{4}\d{7}$/;
    if (!containerPattern.test(trimmedValue)) return 'Enter Correct Container No.';
    if (!['U', 'J', 'Z'].includes(trimmedValue[3])) return 'Enter Correct Container No.';
    return '';
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      setIsExtracting(true);
      
      try {
        const formData = new FormData();
        formData.append('pdf', file);

        // Get and decode token properly
        const encodedToken = localStorage.getItem('token');
        let token = null;
        
        if (encodedToken) {
          try {
            token = atob(atob(encodedToken)); // Double decode
          } catch (error) {
            console.error('Token decode error:', error);
          }
        }

        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${API_BASE_URL}/scraper/extract-pdf`, {
          method: 'POST',
          headers: headers,
          body: formData
        });

        const result = await response.json();

        if (!response.ok || !result?.success) {
          throw new Error(result?.message || 'Failed to extract PDF data');
        }

        const extractedFields = result?.data || {};
        console.log('Extracted Fields:', extractedFields);
        
        // Validate Mode BEFORE opening modal
        const modeValue = extractedFields.mode?.trim().toUpperCase();
        console.log('Mode Validation Check:', modeValue);
        
        if (!modeValue) {
          setIsExtracting(false);
          setPdfFile(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          setErrorTitle('Mode Not Found');
          setErrorMessage('Please upload a valid LEO PDF with Mode information (LAND/SEA).');
          return;
        }
        
        if (modeValue !== 'LAND' && modeValue !== 'SEA') {
          setIsExtracting(false);
          setPdfFile(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          setErrorTitle(`Invalid Shipment Type: ${modeValue}`);
          setErrorMessage(`This appears to be an ${modeValue} shipment. Sea Export only accepts LAND or SEA mode.\n\nPlease use the Air Export feature for AIR shipments.`);
          return;
        }
        
        // Mode is valid - proceed with mapping and opening modal
        // Map API response fields to form fields
        const firstBl = extractedFields.blNumber || '';
        const firstContainer = extractedFields.containerNo || '';
        const initialContainerList = firstContainer
          ? String(firstContainer)
              .split(/\s*&\s*|\s*,\s*/)
              .map(v => v.trim())
              .filter(v => v && v !== '-')
          : [''];

        const mappedData = {
          customerId: extractedFields.customerId || '',
          portCode: extractedFields.portCode || '',
          sbNo: extractedFields.sbNo || '',
          sbDate: extractedFields.sbDate || '',
          pkg: extractedFields.pkg || '',
          gwt: extractedFields.gwt || '',
          portOfLoading: extractedFields.portOfLoading || '',
          stateOfOrigin: extractedFields.stateOfOrigin || '',
          portOfDischarge: extractedFields.portOfDischarge || '',
          portOfDischargeCode: extractedFields.portOfDischargeCode || '',
          portOfFinalDestinationCode: extractedFields.portOfFinalDestinationCode || '',
          exporterName: extractedFields.exporterName || '',
          exporterAddress: extractedFields.exporterAddress || '',
          countryOfFinalDestination: extractedFields.countryOfFinalDestination || '',
          portOfFinalDestination: extractedFields.portOfFinalDestination || '',
          consigneeName: extractedFields.consigneeName || '',
          buyerName: extractedFields.buyerName || '',
          mode: extractedFields.mode || '',
          lut: extractedFields.lut || '',
          fobValue: extractedFields.fobValue || '',
          dbkClaim: extractedFields.dbkClaim || '',
          igstAmt: extractedFields.igstAmt || '',
          cessAmt: extractedFields.cessAmt || '',
          igstValue: extractedFields.igstValue || '',
          rodtepAmt: extractedFields.rodtepAmt || '',
          rosctlAmt: extractedFields.rosctlAmt || '',
          invNo: extractedFields.invNo || extractedFields.invNoDate || extractedFields.invoiceNoDt || '',
          invAmt: extractedFields.invAmt || '',
          currency: extractedFields.currency || '',
          gstNo: (extractedFields.gstNo || extractedFields.gst_no || '').toUpperCase(),
          leoDate: extractedFields.leoDate || '',
          invoiceNoDt: extractedFields.invNoDate || extractedFields.invoiceNoDt || extractedFields.invNo || '',
          blNumber: firstBl,
          containerNo: firstContainer,
          containerQty: '1',
          leoPdf: file
        };
        
        setExtractedData(extractedFields);
        setFormData(mappedData);
        setContainerNoList(initialContainerList.length ? initialContainerList : ['']);
        setIsExtracting(false);
        setShowModal(true);
        
        // Reset file input after successful extraction
        setPdfFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        console.error('PDF extraction failed:', error);
        setErrorTitle('PDF Extraction Failed');
        setErrorMessage('Failed to extract data from PDF. Please try again or check the PDF format.');
        setIsExtracting(false);
        setPdfFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } else {
      setErrorTitle('Invalid File Type');
      setErrorMessage('Please upload a valid PDF file.');
      setPdfFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files, type } = e.target;
    if (type === 'file') {
      if (name === 'leoPdf') {
        const selectedFiles = files ? Array.from(files) : [];
        setFormData(prev => ({
          ...prev,
          leoPdf: selectedFiles.length > 0 ? selectedFiles : null
        }));
        return;
      }
      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
    } else {
      // Convert to uppercase for blNumber and containerNo
      let finalValue = value;
      if ((name === 'blNumber' || name === 'containerNo') && value) {
        finalValue = value.toUpperCase();
      }

      if (name === 'containerQty') {
        const safeQty = Math.max(1, parseInt(value, 10) || 1);
        setContainerNoList(prev => {
          const next = [...(Array.isArray(prev) ? prev : [])];
          while (next.length < safeQty) next.push('');
          return next.slice(0, safeQty);
        });
        setContainerNoItemErrors(prev => {
          const next = [...(Array.isArray(prev) ? prev : [])];
          while (next.length < safeQty) next.push('');
          return next.slice(0, safeQty);
        });
        setFormData(prev => ({
          ...prev,
          containerQty: String(safeQty)
        }));

        validateField(name, String(safeQty));
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: finalValue
      }));
      
      // Trigger real-time validation for specific fields
      if (name === 'blNumber' || name === 'containerNo' || name === 'containerQty') {
        validateField(name, finalValue);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const safeQty = Math.max(1, parseInt(formData.containerQty, 10) || 1);
    const normalizedBlNumber = (formData.blNumber || '').trim();
    const normalizedContainerNos = Array.from({ length: safeQty }, (_, idx) => String(containerNoList[idx] || '').trim().toUpperCase());

    if (safeQty < 1) {
      setFieldErrors(prev => ({
        ...prev,
        containerQty: 'Container Qty cannot be less than 1'
      }));
      return;
    }

    if (!normalizedBlNumber) {
      setFieldErrors(prev => ({
        ...prev,
        blNumber: 'Enter B/L Number'
      }));
      return;
    }

    if (normalizedBlNumber.length < 8) {
      setFieldErrors(prev => ({
        ...prev,
        blNumber: 'B/L Number must be at least 8 characters'
      }));
      return;
    }

    const duplicateBl = existingBookings.find(booking => {
      const existing = (booking.blNumber || '')
        .toString()
        .split(/\s*&\s*|\s*,\s*/)
        .filter(Boolean);

      return existing.some(existingBl => existingBl.toLowerCase() === normalizedBlNumber.toLowerCase());
    });
    if (duplicateBl) {
      setFieldErrors(prev => ({
        ...prev,
        blNumber: `B/L Number "${normalizedBlNumber}" already exists`
      }));
      return;
    }

    const containerPattern = /^[A-Z]{4}\d{7}$/;
    const invalidIndex = normalizedContainerNos.findIndex((cn) => cn && (!containerPattern.test(cn) || !['U', 'J', 'Z'].includes(cn[3])));
    if (invalidIndex !== -1) {
      const itemErrors = Array.from({ length: safeQty }, (_, idx) => (
        idx === invalidIndex ? 'Enter Correct Container No.' : ''
      ));
      setContainerNoItemErrors(itemErrors);
      setFieldErrors(prev => ({
        ...prev,
        containerNo: ''
      }));
      return;
    }

    setContainerNoItemErrors(Array.from({ length: safeQty }, () => ''));

    setFieldErrors({
      blNumber: '',
      containerNo: '',
      containerQty: ''
    });

    // Validate Mode - must be LAND or SEA only (safety check)
    const modeValue = formData.mode?.trim().toUpperCase();
    console.log('Mode Validation Check:', modeValue);

    if (modeValue && modeValue !== 'LAND' && modeValue !== 'SEA') {
      setFieldErrors(prev => ({
        ...prev
      }));
      setErrorTitle('Invalid Mode');
      setErrorMessage(`Only LAND or SEA is allowed for Sea Export.\nThis appears to be an ${modeValue} shipment.`);
      return;
    }
    
    if (!modeValue) {
      setErrorTitle('Mode Required');
      setErrorMessage('Mode is required. Please upload a valid LEO PDF with Mode information.');
      return;
    }

    const sbCount = getSbCount();
    const leoFiles = splitPdfValues(formData.leoPdf);
    if (sbCount > 1 && leoFiles.length > 0 && leoFiles.length !== sbCount) {
      setErrorTitle('LEO PDF Count Mismatch');
      setErrorMessage(`SB count is ${sbCount}. Please upload exactly ${sbCount} LEO PDF files.`);
      return;
    }

    const syncedFormData = {
      ...formData,
      blNumber: normalizedBlNumber,
      containerNo: normalizedContainerNos,
      containerQty: String(safeQty)
    };
    
    // Prepare data for JSON - convert File objects to paths
    const jsonData = {
      ...syncedFormData,
      customerId: selectedCustomer,
      leoPdf: Array.isArray(syncedFormData.leoPdf)
        ? syncedFormData.leoPdf.map(f => f?.name).filter(Boolean).join(' & ')
        : (syncedFormData.leoPdf ? syncedFormData.leoPdf.name : null),
      containerNo: normalizedContainerNos,
      blPdf: syncedFormData.blPdf ? syncedFormData.blPdf.name : null,
      invPlPdf: syncedFormData.invPlPdf ? syncedFormData.invPlPdf.name : null
    };
    
    // Console log complete data as JSON for backend
    console.log('=== SEA EXPORT FORM DATA (JSON) ===');
    console.log(JSON.stringify(jsonData, null, 2));
    console.log('=== END JSON DATA ===');
    
    try {
      setIsSubmittingForm(true);

      // Submit with customer ID included
      await onSubmit({ ...syncedFormData, customerId: selectedCustomer });
      
      // Reset all state
      setShowModal(false);
      setPdfFile(null);
      setExtractedData(null);
      setSelectedCustomer('');
      onCustomerChange('');
      setFieldErrors({
        blNumber: '',
        containerNo: '',
        containerQty: ''
      });
      setContainerNoList(['']);
      setFormData({
        customerId: '',
        portCode: '',
        sbNo: '',
        sbDate: '',
        pkg: '',
        gwt: '',
        portOfLoading: '',
        stateOfOrigin: '',
        portOfDischarge: '',
        portOfDischargeCode: '',
        portOfFinalDestinationCode: '',
        exporterName: '',
        exporterAddress: '',
        countryOfFinalDestination: '',
        portOfFinalDestination: '',
        consigneeName: '',
        buyerName: '',
        mode: '',
        lut: '',
        fobValue: '',
        dbkClaim: '',
        igstAmt: '',
        cessAmt: '',
        igstValue: '',
        rodtepAmt: '',
        rosctlAmt: '',
        invNo: '',
        invAmt: '',
        currency: '',
        gstNo: '',
        leoDate: '',
        invoiceNoDt: '',
        blNumber: '',
        containerNo: '',
        containerQty: '1',
        leoPdf: null,
        blPdf: null,
        invPlPdf: null
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onClose();
    } finally {
      setIsSubmittingForm(false);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setPdfFile(null);
    setExtractedData(null);
    setSelectedCustomer('');
    onCustomerChange('');
    setErrorMessage('');
    setErrorTitle('');
    setFieldErrors({
      blNumber: '',
      containerNo: '',
      containerQty: ''
    });
    setContainerNoList(['']);
    setFormData({
      customerId: '',
      portCode: '',
      sbNo: '',
      sbDate: '',
      pkg: '',
      gwt: '',
      portOfLoading: '',
      stateOfOrigin: '',
      portOfDischarge: '',
      portOfDischargeCode: '',
      portOfFinalDestinationCode: '',
      exporterName: '',
      exporterAddress: '',
      countryOfFinalDestination: '',
      portOfFinalDestination: '',
      consigneeName: '',
      buyerName: '',
      mode: '',
      lut: '',
      fobValue: '',
      dbkClaim: '',
      igstAmt: '',
      cessAmt: '',
      igstValue: '',
      rodtepAmt: '',
      rosctlAmt: '',
      invNo: '',
      invAmt: '',
      currency: '',
      gstNo: '',
      leoDate: '',
      invoiceNoDt: '',
      blNumber: '',
      containerNo: '',
      containerQty: '1',
      leoPdf: null,
      blPdf: null,
      invPlPdf: null
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const trimmedBlNumber = (formData.blNumber || '').trim();
  const allRequiredBlFilled = !!trimmedBlNumber;
  const allRequiredBlValidLength = !trimmedBlNumber || trimmedBlNumber.length >= 8;
  const hasValidationErrors = Object.values(fieldErrors).some(e => e);
  const canSubmitSeaExportForm = allRequiredBlFilled && allRequiredBlValidLength && !hasValidationErrors;

  return (
    <>
      {/* Error Notification */}
      {errorMessage && (
        <div className="fixed top-4 right-4 left-4 md:left-auto z-50 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 rounded-lg shadow-lg animate-slide-in" style={{maxWidth: 'min(90vw, 384px)'}}>
          <div className="p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-bold text-red-800 dark:text-red-200">
                  {errorTitle || 'Error'}
                </h3>
                <div className="mt-1 text-sm text-red-700 dark:text-red-300 whitespace-pre-line">
                  {errorMessage}
                </div>
              </div>
              <button
                onClick={() => {
                  setErrorMessage('');
                  setErrorTitle('');
                }}
                className="ml-3 flex-shrink-0 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200 transition"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Initial Form - Customer Selection + PDF Upload */}
      {!showModal && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-4 md:p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Sea Export - Upload LEO/Shipping Bill</h3>
          </div>

          <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-2 gap-3 sm:gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Select User <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedCustomer}
                onChange={(e) => {
                  setSelectedCustomer(e.target.value);
                  onCustomerChange(e.target.value);
                }}
                className="ql-select w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 transition"
                required
              >
                <option value="">Select User</option>
                {customers.map((customer, index) => (
                  <option key={`${customer.id}-${customer.name || customer.username || 'customer'}-${index}`} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Upload LEO/SB PDF <span className="text-red-500">*</span>
              </label>
              <label className={`flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-dashed rounded-lg cursor-pointer transition ${
                selectedCustomer
                  ? 'border-cyan-400 dark:border-cyan-600 hover:bg-cyan-50 dark:hover:bg-cyan-900/20'
                  : 'border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 cursor-not-allowed'
              }`}>
                <Upload size={20} className={selectedCustomer ? 'text-cyan-500' : 'text-slate-400'} />
                <span className={`text-sm font-medium ${selectedCustomer ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400'}`}>
                  {pdfFile ? pdfFile.name : 'Choose PDF File'}
                </span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileUpload}
                  disabled={!selectedCustomer}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {isExtracting && pdfFile && (
            <div className="mt-4 flex items-center gap-3 p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan200 dark:border-cyan-800">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-600"></div>
              <div className="flex-1">
                <p className="font-medium text-cyan-800 dark:text-cyan-200">Extracting data from PDF...</p>
                <p className="text-sm text-cyan-600 dark:text-cyan-400">Processing first 2 pages</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal Form */}
      {showModal && extractedData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-3 sm:p-4">
          <div 
            className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl w-full max-h-[95vh] overflow-y-auto"
            style={{
              maxWidth: 'min(95vw, 900px)',
              scrollbarWidth: 'thin',
              scrollbarColor: '#2563eb #e5e7eb'
            }}
          >
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 p-3 sm:p-6 rounded-t-lg z-10">
              <div className="flex items-start sm:items-center justify-between gap-3">
                <div className="flex items-start sm:items-center gap-2 sm:gap-3 flex-1 min-w-0">
                  <FileText className="text-white flex-shrink-0 hidden sm:block" size={28} />
                  <div className="min-w-0">
                    <h2 className="text-lg sm:text-2xl font-bold text-white break-words">Sea Export - Shipment Details</h2>
                  </div>
                </div>
                <button
                  onClick={handleCancel}
                  type="button"
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-3 sm:p-6 space-y-4 sm:space-y-5">
              
              {/* Manual Entry Section */}
              <div className="border-t border-slate-200 dark:border-slate-700 pt-4 sm:pt-5">
                <h3 className="text-base sm:text-lg font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
                  <div className="w-1.5 h-6 bg-gradient-to-b from-purple-600 to-pink-600 rounded"></div>
                  Enter Required Details
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5 text-left">
                      B/L Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="blNumber"
                      value={formData.blNumber}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
                      placeholder="Enter B/L Number"
                    />
                    {fieldErrors.blNumber && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-medium">⚠️ {fieldErrors.blNumber}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5 text-left">
                      Container Qty <span className="text-red-500">*</span>
                    </label>
                    <div className={`flex items-center overflow-hidden rounded-xl border ${
                      fieldErrors.containerQty
                        ? 'border-red-500 dark:border-red-500'
                        : 'border-slate-300 dark:border-slate-600'
                    }`}>
                      <button
                        type="button"
                        onClick={() => handleInputChange({ target: { name: 'containerQty', value: String(Math.max(1, (parseInt(formData.containerQty, 10) || 1) - 1)), type: 'text' } })}
                        className="h-11 w-14 min-w-[56px] shrink-0 bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center transition"
                        aria-label="Decrease container quantity"
                      >
                        <Minus size={16} strokeWidth={3} />
                      </button>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        name="containerQty"
                        value={formData.containerQty}
                        onChange={handleInputChange}
                        className={`h-11 flex-1 min-w-0 px-2 text-center text-base font-semibold outline-none ${
                          fieldErrors.containerQty
                            ? 'bg-red-50 dark:bg-red-900/20 text-slate-900 dark:text-white'
                            : 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => handleInputChange({ target: { name: 'containerQty', value: String((parseInt(formData.containerQty, 10) || 1) + 1), type: 'text' } })}
                        className="h-11 w-14 min-w-[56px] shrink-0 bg-indigo-600 hover:bg-indigo-700 text-white flex items-center justify-center transition"
                        aria-label="Increase container quantity"
                      >
                        <Plus size={16} strokeWidth={3} />
                      </button>
                    </div>
                    {fieldErrors.containerQty ? (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-medium">⚠️ {fieldErrors.containerQty}</p>
                    ) : (
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Minimum value: 1</p>
                    )}
                  </div>

                  <div className={Math.max(1, parseInt(formData.containerQty, 10) || 1) > 1 ? 'md:col-span-3' : ''}>
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5 text-left">Container No</label>
                    <div className={`grid gap-2 ${Math.max(1, parseInt(formData.containerQty, 10) || 1) > 1 ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3' : 'grid-cols-1'}`}>
                      {Array.from({ length: Math.max(1, parseInt(formData.containerQty, 10) || 1) }, (_, idx) => (
                        <div key={idx}>
                          <input
                            type="text"
                            maxLength="11"
                            value={containerNoList[idx] || ''}
                            onChange={(e) => {
                              const v = (e.target.value || '').toUpperCase();
                              setContainerNoList(prev => {
                                const qty = Math.max(1, parseInt(formData.containerQty, 10) || 1);
                                const next = [...(Array.isArray(prev) ? prev : [])];
                                while (next.length < qty) next.push('');
                                next[idx] = v;
                                return next;
                              });

                              const itemError = validateContainerNoValue(v);
                              setContainerNoItemErrors(prev => {
                                const qty = Math.max(1, parseInt(formData.containerQty, 10) || 1);
                                const next = [...(Array.isArray(prev) ? prev : [])];
                                while (next.length < qty) next.push('');
                                next[idx] = itemError;
                                return next;
                              });
                            }}
                            className={`w-full px-3 py-2.5 border rounded-lg transition ${
                              containerNoItemErrors[idx]
                                ? 'border-red-500 dark:border-red-500 bg-red-50 dark:bg-red-900/20 text-slate-900 dark:text-white focus:border-red-600 focus:ring-2 focus:ring-red-200'
                                : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200'
                            }`}
                            placeholder={`Container No ${idx + 1} (ABCU1234567)`}
                          />
                          {containerNoItemErrors[idx] && (
                            <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-medium">⚠️ {containerNoItemErrors[idx]}</p>
                          )}
                        </div>
                      ))}
                    </div>
                    {containerNoItemErrors.every(err => !err) && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Container No fields are auto-generated based on Container Qty.</p>
                    )}
                  </div>
                </div>

                {/* PDF Uploads */}
                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">B/L PDF</label>
                    {formData.blPdf ? (
                      <div className="p-3 bg-green-50 dark:bg-green-900/25 border-2 border-green-200 dark:border-green-700/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <a 
                            href={URL.createObjectURL(formData.blPdf)} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex-1 px-3 py-2 text-sm font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-800 hover:bg-green-200 dark:hover:bg-green-700 rounded-md transition flex items-center justify-center gap-2"
                          >
                            <FileText size={18} className="flex-shrink-0" />
                            View
                          </a>
                          <button
                            type="button"
                            onClick={() => openPdfRemoveConfirm({
                              title: 'Remove PDF?',
                              message: 'Are you sure you want to remove this B/L PDF?',
                              onConfirm: () => setFormData(prev => ({ ...prev, blPdf: null }))
                            })}
                            className="px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-800/60 rounded-md transition flex items-center gap-1"
                          >
                            <X size={18} className="flex-shrink-0" />
                            Clear
                          </button>
                        </div>
                      </div>
                    ) : (
                      <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-cyan-400">
                        <Upload size={16} className="text-slate-500" />
                        <span className="text-xs text-slate-600">Upload PDF</span>
                        <input type="file" name="blPdf" accept=".pdf" onChange={handleInputChange} className="hidden" />
                      </label>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">LEO PDF</label>
                    {splitPdfValues(formData.leoPdf).length > 0 ? (
                      <div className="p-3 bg-green-50 dark:bg-green-900/25 border-2 border-green-200 dark:border-green-700/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <a 
                            href={splitPdfValues(formData.leoPdf)[0] instanceof File ? URL.createObjectURL(splitPdfValues(formData.leoPdf)[0]) : '#'} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex-1 px-3 py-2 text-sm font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-800 hover:bg-green-200 dark:hover:bg-green-700 rounded-md transition flex items-center justify-center gap-2"
                          >
                            <FileText size={18} className="flex-shrink-0" />
                            {splitPdfValues(formData.leoPdf).length > 1 ? `View (${splitPdfValues(formData.leoPdf).length})` : 'View'}
                          </a>
                          <button
                            type="button"
                            onClick={() => openPdfRemoveConfirm({
                              title: 'Remove PDF?',
                              message: 'Are you sure you want to remove this LEO PDF?',
                              onConfirm: () => setFormData(prev => ({ ...prev, leoPdf: null }))
                            })}
                            className="px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-800/60 rounded-md transition flex items-center gap-1"
                          >
                            <X size={18} className="flex-shrink-0" />
                            Clear
                          </button>
                        </div>
                      </div>
                    ) : (
                      <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-cyan-400">
                        <Upload size={16} className="text-slate-500" />
                        <span className="text-xs text-slate-600">Upload PDF{getSbCount() > 1 ? ` (${getSbCount()})` : ''}</span>
                        <input type="file" name="leoPdf" accept=".pdf" multiple={getSbCount() > 1} onChange={handleInputChange} className="hidden" />
                      </label>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">INV/PL PDF</label>
                    {formData.invPlPdf ? (
                      <div className="p-3 bg-green-50 dark:bg-green-900/25 border-2 border-green-200 dark:border-green-700/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <a 
                            href={URL.createObjectURL(formData.invPlPdf)} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex-1 px-3 py-2 text-sm font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-800 hover:bg-green-200 dark:hover:bg-green-700 rounded-md transition flex items-center justify-center gap-2"
                          >
                            <FileText size={18} className="flex-shrink-0" />
                            View
                          </a>
                          <button
                            type="button"
                            onClick={() => openPdfRemoveConfirm({
                              title: 'Remove PDF?',
                              message: 'Are you sure you want to remove this INV/PL PDF?',
                              onConfirm: () => setFormData(prev => ({ ...prev, invPlPdf: null }))
                            })}
                            className="px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-800/60 rounded-md transition flex items-center gap-1"
                          >
                            <X size={18} className="flex-shrink-0" />
                            Clear
                          </button>
                        </div>
                      </div>
                    ) : (
                      <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-cyan-400">
                        <Upload size={16} className="text-slate-500" />
                        <span className="text-xs text-slate-600">Upload PDF</span>
                        <input type="file" name="invPlPdf" accept=".pdf" onChange={handleInputChange} className="hidden" />
                      </label>
                    )}
                  </div>
                </div>
              </div>

              {/* Submit and Cancel Buttons */}
              <div className="pt-6 border-t-2 border-slate-200 dark:border-slate-700 space-y-3">
                {isSubmittingForm && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm text-blue-600 dark:text-blue-400 font-medium">
                      <span>Submitting sea shipment...</span>
                      <span>Please wait</span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-blue-100 dark:bg-slate-700">
                      <div className="h-full w-1/3 rounded-full bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 animate-[seaSubmitLinearLoad_1.2s_ease-in-out_infinite]"></div>
                    </div>
                    <style>{`
                      @keyframes seaSubmitLinearLoad {
                        0% {
                          transform: translateX(-120%);
                        }
                        100% {
                          transform: translateX(320%);
                        }
                      }
                    `}</style>
                  </div>
                )}
                <div className="flex gap-4">
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isSubmittingForm}
                  className="flex-1 px-6 py-3.5 border-2 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-xl font-semibold hover:bg-slate-100 dark:hover:bg-slate-700 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!canSubmitSeaExportForm || isSubmittingForm}
                  title={!allRequiredBlFilled ? 'Please fill B/L Number field' : ''}
                  className={`flex-1 px-6 py-3.5 rounded-xl font-semibold shadow-lg flex items-center justify-center gap-2 transition ${
                    (!canSubmitSeaExportForm || isSubmittingForm)
                      ? 'bg-slate-300 dark:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-not-allowed opacity-50'
                      : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white'
                  }`}
                >
                  {isSubmittingForm ? (
                    'Submitting...'
                  ) : (
                    <>
                      <Save size={20} />
                      Submit
                    </>
                  )}
                </button>
              </div>
              </div>

              {/* PDF Extracted Data Section */}
              <div className="border-t border-slate-200 dark:border-slate-700 pt-5">
                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Port Code</label><input type="text" name="portCode" value={formData.portCode} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">SB No</label><input type="text" name="sbNo" value={formData.sbNo} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">SB Date</label><input type="text" name="sbDate" value={formData.sbDate} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Mode</label><input type="text" name="mode" value={formData.mode} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">LUT</label><input type="text" name="lut" value={formData.lut} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">PKG</label><input type="text" name="pkg" value={formData.pkg} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">G.WT</label><input type="text" name="gwt" value={formData.gwt} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">PORT OF LOADING</label><input type="text" name="portOfLoading" value={formData.portOfLoading} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">STATE OF ORIGIN</label><input type="text" name="stateOfOrigin" value={formData.stateOfOrigin} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">PORT OF DISCHARGE CODE</label><input type="text" name="portOfDischargeCode" value={formData.portOfDischargeCode} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">PORT OF DISCHARGE</label><input type="text" name="portOfDischarge" value={formData.portOfDischarge} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">COUNTRY OF FINAL DESTINATION</label><input type="text" name="countryOfFinalDestination" value={formData.countryOfFinalDestination} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">PORT OF FINAL DESTINATION CODE</label><input type="text" name="portOfFinalDestinationCode" value={formData.portOfFinalDestinationCode} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">PORT OF FINAL DESTINATION</label><input type="text" name="portOfFinalDestination" value={formData.portOfFinalDestination} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">EXPORTER NAME</label><input type="text" name="exporterName" value={formData.exporterName} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">BUYER NAME</label><input type="text" name="buyerName" value={formData.buyerName} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">CONSIGNEE NAME</label><input type="text" name="consigneeName" value={formData.consigneeName} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">FOB VALUE</label><input type="text" name="fobValue" value={formData.fobValue} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">DBK CLAIM</label><input type="text" name="dbkClaim" value={formData.dbkClaim} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">IGST AMT</label><input type="text" name="igstAmt" value={formData.igstAmt} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">CESS AMT</label><input type="text" name="cessAmt" value={formData.cessAmt} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">RODTEP AMT</label><input type="text" name="rodtepAmt" value={formData.rodtepAmt} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">ROSCTL AMT</label><input type="text" name="rosctlAmt" value={formData.rosctlAmt} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">INV AMT.</label><input type="text" name="invAmt" value={formData.invAmt} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">CURRENCY</label><input type="text" name="currency" value={formData.currency} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">LEO Date</label><input type="text" name="leoDate" value={formData.leoDate} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">INVOICE No. & Dt.</label><input type="text" name="invoiceNoDt" value={formData.invoiceNoDt} readOnly className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed" /></div>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {pdfRemoveConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-14 h-14 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <AlertTriangle className="text-red-600 dark:text-red-400" size={28} />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white text-center">
                {pdfRemoveConfirm.title || 'Remove PDF?'}
              </h3>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 text-center">
                {pdfRemoveConfirm.message || 'Are you sure you want to remove this PDF?'}
              </p>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={closePdfRemoveConfirm}
                  className="flex-1 px-4 py-2.5 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 rounded-lg transition font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmPdfRemove}
                  className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition font-medium"
                >
                  Yes, Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SeaExportForm;
